import { useState, useEffect } from 'react';
import {
  Option,
  StreamMetadataDetails,
} from 'containers/PodHomePage/components/Screen/components/OnDemandModal/types';

interface UseFormValidationProps {
  activeTab: 'manual' | 'mosaic' | 'webTester';
  primaryAsset: File | null;
  primaryAssetError?: string;
  secondaryAsset: File | null;
  destinationAsset: File | null;
  selectedAdType: Option | null;
  selectedDestinationType: Option | null;
  selectedAppId: Option | null;
  contentTargetValue: string;
  destinationURL: string;
  selectedAdslot: Option | null;
  selectedAdslots?: Option[];
  selectedContentPartner: Option[];
  campaignID: string;
  creativeID: string;
  adslotState: boolean;
  creativeAdslots: Option[];
  validationStatus: boolean | undefined;
  validateCheck?: any;
  osv: Option[] | null;
  selectedCountry: Option | null;
  mandatoryFields: string;
  setMandatoryFields: (value: string) => void;
  clickableStreamUrl: string;
  clickableStreamMetadata: StreamMetadataDetails | null;
  clickableStreamError: string;
  optionalStreamUrl: string;
  optionalStreamMetadata: StreamMetadataDetails | null;
  optionalStreamError: string;
  destinationStreamUrl: string;
  destinationStreamMetadata: StreamMetadataDetails | null;
  destinationStreamError: string;
  batchItemCount?: number;
}

export const useFormValidation = (props: UseFormValidationProps) => {
  const {
    activeTab,
    primaryAsset,
    primaryAssetError = '',
    secondaryAsset,
    destinationAsset,
    selectedAdType,
    selectedDestinationType,
    selectedAppId,
    contentTargetValue,
    destinationURL,
    selectedAdslot,
    selectedAdslots = [],
    selectedContentPartner,
    campaignID,
    creativeID,
    adslotState,
    creativeAdslots,
    validationStatus,
    validateCheck = {},
    osv,
    selectedCountry,
    setMandatoryFields,
    mandatoryFields,
    clickableStreamUrl,
    clickableStreamMetadata,
    clickableStreamError,
    optionalStreamUrl,
    optionalStreamMetadata,
    optionalStreamError,
    destinationStreamUrl,
    destinationStreamMetadata,
    destinationStreamError,
    batchItemCount = 0,
  } = props;

  const [saveStatus, setSaveStatus] = useState(false);
  const [error, setError] = useState('');

  const validateUrl = (value: string) => {
    if (value.trim() === '') {
      setError('URL cannot be empty');
      return false;
    }
    try {
      new URL(value);
      setError('');
      return true;
    } catch {
      setError('Invalid URL');
      return false;
    }
  };

  const checkAdtype = () => {
    if (
      selectedAdType?.value === 'companionTo' ||
      selectedAdType?.value === 'clickableVideoTo'
    ) {
      if (secondaryAsset) {
        setMandatoryFields('');
        setSaveStatus(true);
        return true;
      }
      setMandatoryFields('Please upload the secondary asset to proceed.');
      setSaveStatus(false);
      return false;
    }
    setMandatoryFields('');
    setSaveStatus(true);
    return true;
  };

  const checkAdslot = () => {
    const adslotsToValidate =
      activeTab === 'manual' && selectedAdslots.length > 0
        ? selectedAdslots
        : selectedAdslot
          ? [selectedAdslot]
          : [];

    if (adslotsToValidate.length > 0) {
      if (
        adslotsToValidate.some(
          adslot => adslot.label === 'portal/screensaver@main',
        )
      ) {
        if (selectedContentPartner && selectedContentPartner.length > 0) {
          return checkAdtype();
        }
        setMandatoryFields('Please select Content Partner to proceed.');
        setSaveStatus(false);
        return false;
      }
      return checkAdtype();
    }

    if (!validationStatus) {
      setMandatoryFields('');
      setSaveStatus(false);
      return false;
    }

    setMandatoryFields('Please select Ad Slot to proceed.');
    setSaveStatus(false);
    return false;
  };

  const getAdslotName = (adslot: Option) => adslot.value || adslot.label;

  const fieldSupportsValueForAllSelectedAdslots = (
    field: string,
    value: string,
  ) =>
    selectedAdslots.every(adslot =>
      (validateCheck?.[getAdslotName(adslot)]?.[field] || []).includes(value),
    );

  const getUnsupportedTargetingFields = () => {
    const unsupportedFields: string[] = [];
    if (selectedAdslots.length === 0) return unsupportedFields;

    if (
      selectedAdType &&
      !fieldSupportsValueForAllSelectedAdslots('adType', selectedAdType.value)
    ) {
      unsupportedFields.push('Ad Type');
    }

    if (
      selectedDestinationType &&
      !fieldSupportsValueForAllSelectedAdslots(
        'destinationType',
        selectedDestinationType.value,
      )
    ) {
      unsupportedFields.push('Destination Type');
    }

    if (
      selectedCountry &&
      !fieldSupportsValueForAllSelectedAdslots(
        'countryCode',
        selectedCountry.value,
      )
    ) {
      unsupportedFields.push('Country');
    }

    if (
      osv?.some(
        option =>
          !fieldSupportsValueForAllSelectedAdslots('osvs', option.value),
      )
    ) {
      unsupportedFields.push('OS');
    }

    return unsupportedFields;
  };

  useEffect(() => {
    if (activeTab === 'webTester') {
      if (!selectedDestinationType) {
        setMandatoryFields('Please select Destination Type to proceed.');
        setSaveStatus(false);
        return;
      }
      if (!destinationURL.trim()) {
        setMandatoryFields('Please enter Destination URL to proceed.');
        setSaveStatus(false);
        return;
      }
      if (!validateUrl(destinationURL)) {
        setMandatoryFields('Please enter a valid Destination URL to proceed.');
        setSaveStatus(false);
        return;
      }
      if (!selectedCountry) {
        setMandatoryFields('Please select Country to proceed.');
        setSaveStatus(false);
        return;
      }
      if (!osv || osv.length === 0) {
        setMandatoryFields('Please select OS Versions to proceed.');
        setSaveStatus(false);
        return;
      }
      setMandatoryFields('');
      setSaveStatus(true);
      return;
    }

    if (activeTab !== 'manual') {
      if (campaignID.trim() !== '') {
        if (creativeID.trim() === '') {
          setMandatoryFields('Please enter Creative ID to proceed.');
          setSaveStatus(false);
          return;
        }
        if (!creativeAdslots || creativeAdslots.length === 0) {
          setMandatoryFields('Please click on Validate to proceed.');
          setSaveStatus(false);
          return;
        }
      }
      if (adslotState && !selectedAdslot) {
        setMandatoryFields('Please select Ad Slot to proceed.');
        setSaveStatus(false);
        return;
      }
      if (adslotState && selectedAdslot) {
        setMandatoryFields('');
        setSaveStatus(true);
        return;
      }
      if (campaignID.trim() === '') {
        setMandatoryFields('Please enter Campaign ID to proceed.');
        return;
      }
      return;
    }

    if (!primaryAsset) {
      setMandatoryFields(
        batchItemCount > 0 ? '' : 'Please upload the primary asset to proceed.',
      );
      setSaveStatus(false);
      return;
    }
    if (primaryAssetError) {
      setMandatoryFields(primaryAssetError);
      setSaveStatus(false);
      return;
    }
    if (validationStatus === false) {
      setMandatoryFields('');
      setSaveStatus(false);
      return;
    }
    if (selectedAdslots.length === 0) {
      setMandatoryFields('Please select at least one Ad Slot to proceed.');
      setSaveStatus(false);
      return;
    }
    if (!validationStatus) {
      setMandatoryFields('Please click on Validate to proceed.');
      setSaveStatus(false);
      return;
    }
    if (!selectedAdType) {
      setMandatoryFields('Please select Ad Type to proceed.');
      setSaveStatus(false);
      return;
    }
    if (!selectedDestinationType) {
      setMandatoryFields('Please select Destination Type to proceed.');
      setSaveStatus(false);
      return;
    }
    if (!selectedCountry) {
      setMandatoryFields('Please select Country to proceed.');
      setSaveStatus(false);
      return;
    }
    if (!osv || osv.length === 0) {
      setMandatoryFields('Please select OS Versions to proceed.');
      setSaveStatus(false);
      return;
    }

    if (selectedAdType.value === 'clickableStreamTo') {
      if (!clickableStreamUrl.trim()) {
        setMandatoryFields('Please enter Stream URL to proceed.');
        setSaveStatus(false);
        return;
      }
      if (clickableStreamError || !clickableStreamMetadata) {
        setMandatoryFields(
          clickableStreamError || 'Please validate the Stream URL to proceed.',
        );
        setSaveStatus(false);
        return;
      }
    }

    if (selectedAdType.value === 'expandableBannerTo') {
      if (optionalStreamUrl.trim() && secondaryAsset) {
        setMandatoryFields(
          'Please use either an optional hover video asset or a stream URL, not both.',
        );
        setSaveStatus(false);
        return;
      }
      if (
        optionalStreamUrl.trim() &&
        (optionalStreamError || !optionalStreamMetadata)
      ) {
        setMandatoryFields(
          optionalStreamError ||
            'Please validate the optional hover stream URL to proceed.',
        );
        setSaveStatus(false);
        return;
      }
    }

    const unsupportedTargetingFields = getUnsupportedTargetingFields();
    if (unsupportedTargetingFields.length > 0) {
      setMandatoryFields(
        `Selected ${unsupportedTargetingFields.join(
          ', ',
        )} not supported by all selected Ad Slots.`,
      );
      setSaveStatus(false);
      return;
    }

    switch (selectedDestinationType.value) {
      case 'App':
        if (!selectedAppId) {
          setMandatoryFields('Please select App Group Name to proceed.');
          setSaveStatus(false);
          return;
        }
        break;
      case 'Deeplink':
        if (!selectedAppId || !contentTargetValue.trim()) {
          setMandatoryFields(
            'Please select App Group Name and enter Content Target to proceed.',
          );
          setSaveStatus(false);
          return;
        }
        break;
      case 'Image':
      case 'Video':
        if (!destinationAsset) {
          setMandatoryFields(
            `Please upload the destination ${selectedDestinationType.value} asset to proceed.`,
          );
          setSaveStatus(false);
          return;
        }
        break;
      case 'Stream':
        if (!destinationStreamUrl.trim()) {
          setMandatoryFields('Please enter Stream URL to proceed.');
          setSaveStatus(false);
          return;
        }
        if (destinationStreamError || !destinationStreamMetadata) {
          setMandatoryFields(
            destinationStreamError ||
              'Please validate the Stream URL to proceed.',
          );
          setSaveStatus(false);
          return;
        }
        break;
      case 'Web':
      case 'WebFS':
        if (!destinationURL.trim()) {
          setMandatoryFields('Please enter Destination URL to proceed.');
          setSaveStatus(false);
          return;
        }
        if (!validateUrl(destinationURL)) {
          setMandatoryFields(
            'Please enter a valid Destination URL to proceed.',
          );
          setSaveStatus(false);
          return;
        }
        break;
      default:
        setSaveStatus(false);
        return;
    }

    checkAdslot();
  }, [
    activeTab,
    primaryAsset,
    primaryAssetError,
    secondaryAsset,
    destinationAsset,
    selectedAdType,
    selectedDestinationType,
    selectedAppId,
    contentTargetValue,
    destinationURL,
    selectedAdslot,
    selectedAdslots,
    selectedContentPartner,
    campaignID,
    creativeID,
    adslotState,
    creativeAdslots,
    validationStatus,
    validateCheck,
    selectedCountry,
    osv,
    clickableStreamUrl,
    clickableStreamMetadata,
    clickableStreamError,
    optionalStreamUrl,
    optionalStreamMetadata,
    optionalStreamError,
    destinationStreamUrl,
    destinationStreamMetadata,
    destinationStreamError,
    batchItemCount,
  ]);

  return {
    setMandatoryFields,
    mandatoryFields,
    saveStatus,
    error,
    validateUrl,
  };
};
